import { useState } from "react";
import { Wand2, Loader2, Share2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { localDate } from "@/lib/dates";
import { generateSuitInsightImage } from "@/lib/suitInsightImage";

export const SuitInsight = ({ token, palette }) => {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  const share = async () => {
    try {
      const blob = await generateSuitInsightImage(insight);
      const file = new File([blob], "guiding-suit.png", { type: "image/png" });
      const text = `This season, my guiding suit is ${insight.suit} 🌼 Be The Light · House of Bloom ${window.location.origin}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text, title: "My Guiding Suit" });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guiding-suit.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Insight card downloaded — ready to post 🌼");
    } catch (e) {
      toast.error("Couldn't create the insight image");
    }
  };

  const fetchInsight = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lightdeck/suit-insight?date=${localDate()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsight(res.data);
    } catch (e) {
      toast.error("Spirit is resting — try again in a moment");
    } finally {
      setLoading(false);
    }
  };

  if (!insight) {
    return (
      <button
        onClick={fetchInsight}
        disabled={loading}
        className="w-full mt-3 rounded-xl border-2 border-dashed py-3 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
        style={{ borderColor: `${palette.primary}55`, color: palette.primary, opacity: loading ? 0.6 : 1 }}
        data-testid="light-deck-insight-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        {loading ? "Listening to your season…" : "What is this season teaching me?"}
      </button>
    );
  }

  if (!insight.ready) {
    return (
      <p className="mt-3 text-xs text-center rounded-xl p-3" style={{ backgroundColor: `${palette.primary}0D`, color: palette.primary }} data-testid="light-deck-insight-notready">
        Draw {insight.draws_needed} more card{insight.draws_needed === 1 ? "" : "s"} and your guiding suit will reveal itself ✨
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-xl p-4" style={{ backgroundColor: `${palette.primary}0D`, border: `1px solid ${palette.primary}33` }} data-testid="light-deck-suit-insight">
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#B08A3E" }}>
        Your guiding suit · {insight.suit}
      </p>
      <p className="text-sm leading-relaxed italic" style={{ color: palette.text }}>{insight.insight}</p>
      <button
        onClick={share}
        className="mt-3 px-4 py-2 rounded-full text-xs font-bold inline-flex items-center gap-1.5 text-white"
        style={{ backgroundColor: "#D97706" }}
        data-testid="suit-insight-share-btn"
      >
        <Share2 className="w-3.5 h-3.5" /> Share this insight
      </button>
    </div>
  );
};
